import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useSelector, useDispatch } from "react-redux";
import { ErrorToast, SuccesToast } from "../../../components/Toast/message";
import { endPoints } from "../../../constant/Environment";
import { addData } from "../../../Utility/API";

const KycProcess = ({ onKycSuccess }) => {
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);
  const IsLoginData = authState?.user;

  const [selfieFile, setSelfieFile] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [backDocumentFile, setBackDocumentFile] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [backDocumentPreview, setBackDocumentPreview] = useState(null);
  const [selfieUrl, setSelfieUrl] = useState(null);
  const [frontUrl, setFrontUrl] = useState(null);
  const [backUrl, setBackUrl] = useState(null);
  const [docType, setDocType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clearDocument = (e, fileType) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileType === "selfie") {
      setSelfiePreview(null);
      setSelfieFile(null);
      setSelfieUrl(null);
      const input = document.getElementById("selfie-upload");
      if (input) input.value = "";
    } else if (fileType === "document") {
      setDocumentPreview(null);
      setDocumentFile(null);
      setFrontUrl(null);
      const input = document.getElementById("document-upload");
      if (input) input.value = "";
    } else if (fileType === "backDocument") {
      setBackDocumentPreview(null);
      setBackDocumentFile(null);
      setBackUrl(null);
      const input = document.getElementById("back-document-upload");
      if (input) input.value = "";
    }
  };

  const onImageChange = (event, fileType) => {
    const { files } = event.target;
    const file = files[0];

    if (!file) return;

    // Validate file type - only images
    if (!file.name.match(/\.(jpg|jpeg|png|gif)$/)) {
      ErrorToast("Please select valid image file.");
      return false;
    }

    // Validate file size (2MB max)
    const MAX_FILE_SIZE = 1024; // 1KB
    const fileSizeKiloBytes = file.size / MAX_FILE_SIZE;
    if (fileSizeKiloBytes > 2048) {
      ErrorToast("File size is greater than 2MB. Please upload again.");
      return false;
    }

    // Create preview URL for images
    if (file.type.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(file);
      if (fileType === "selfie") {
        setSelfiePreview(previewUrl);
        setSelfieFile(file);
      } else if (fileType === "document") {
        setDocumentPreview(previewUrl);
        setDocumentFile(file);
      } else if (fileType === "backDocument") {
        setBackDocumentPreview(previewUrl);
        setBackDocumentFile(file);
      }
      // Upload image immediately
      UploadImage(file, fileType);
    }
  };

  const UploadImage = (file, fileType) => {
    var formdata = new FormData();
    formdata.append("file", file);
    var requestOptions = {
      method: "POST",
      body: formdata,
      redirect: "follow",
    };
    let imgUrl = `${endPoints.apiBaseUrl}${endPoints.api.KYC_UPLOAD}`;
    fetch(imgUrl, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (result.status === "success") {
          SuccesToast(result.message || "Image uploaded successfully");
          // Store the uploaded image URL
          if (fileType === "selfie") {
            setSelfieUrl(result.data);
          } else if (fileType === "document") {
            setFrontUrl(result.data);
          } else if (fileType === "backDocument") {
            setBackUrl(result.data);
          }
        } else {
          ErrorToast(result.message || "Image upload failed");
          // Clear preview and file if upload failed
          if (fileType === "selfie") {
            setSelfiePreview(null);
            setSelfieFile(null);
            document.getElementById("selfie-upload").value = "";
          } else if (fileType === "document") {
            setDocumentPreview(null);
            setDocumentFile(null);
            document.getElementById("document-upload").value = "";
          } else if (fileType === "backDocument") {
            setBackDocumentPreview(null);
            setBackDocumentFile(null);
            document.getElementById("back-document-upload").value = "";
          }
        }
      })
      .catch((error) => {
        ErrorToast("Error uploading image");
        // Clear preview and file on error
        if (fileType === "selfie") {
          setSelfiePreview(null);
          setSelfieFile(null);
          document.getElementById("selfie-upload").value = "";
        } else if (fileType === "document") {
          setDocumentPreview(null);
          setDocumentFile(null);
          document.getElementById("document-upload").value = "";
        } else if (fileType === "backDocument") {
          setBackDocumentPreview(null);
          setBackDocumentFile(null);
          document.getElementById("back-document-upload").value = "";
        }
        console.log("error", error);
      });
  };

  const saveKyc = async () => {
    const payload = {
      idUser: IsLoginData?.idUser,
      selfie: selfieUrl,
      front: frontUrl,
      back: backUrl,
      doc_type: docType,
    };
    const url = `${endPoints.api.KYC_SAVE}`;
    try {
      setIsSubmitting(true);
      const response = await addData(url, payload);
      console.log("KYC save response:", response);
      if (response?.data?.status === "success") {
        SuccesToast(response?.data?.message || "KYC verification submitted successfully");
        
        // Reset form
        setSelfieFile(null);
        setDocumentFile(null);
        setBackDocumentFile(null);
        setSelfiePreview(null);
        setDocumentPreview(null);
        setBackDocumentPreview(null);
        setSelfieUrl(null);
        setFrontUrl(null);
        setBackUrl(null);
        setDocType("");
        
        // Reset file inputs
        const selfieInput = document.getElementById("selfie-upload");
        const documentInput = document.getElementById("document-upload");
        const backDocumentInput = document.getElementById("back-document-upload");
        if (selfieInput) selfieInput.value = "";
        if (documentInput) documentInput.value = "";
        if (backDocumentInput) backDocumentInput.value = "";

        // Call success callback if provided
        if (onKycSuccess) {
          onKycSuccess();
        }
      } else {
        ErrorToast(response?.data?.message || "Failed to upload KYC information");
      }
    } catch (error) {
      console.error("Error uploading KYC information:", error);
      ErrorToast("An error occurred while uploading KYC information");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKycSubmit = async () => {
    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    if (!selfieUrl || !frontUrl || !backUrl) {
      ErrorToast("Please upload selfie, front and back of document");
      return;
    }

    if (!docType) {
      ErrorToast("Please select document type");
      return;
    }

    // Save KYC with all documents
    await saveKyc();
  };

  return (
    <Card
      className="profile_card"
      style={{ marginBottom: 24, marginTop: 30 }}
      sx={{
        // backgroundColor: "#0d1b2a",
      }}
    >
      <CardContent>
        <Typography variant="h4" align="left" gutterBottom>
          Verify KYC Process
        </Typography>
        <Typography className="form_p" variant="subtitle2">
          Please upload a clear selfie and a valid ID (Driving License or
          Passport) - both front and back sides for identity verification.
        </Typography>

        <div>
          <div className="forget_div mb-2">
            <Typography variant="subtitle1" align="left">
              Upload Selfie
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{
                marginBottom: 2,
                borderColor: "#4caf50",
                color: "black",
                textTransform: "capitalize",
                justifyContent: "space-between",
                padding: "10px",
              }}
            >
              <span>{selfieFile ? selfieFile.name : "Choose File"}</span>
              {selfieFile && (
                <ClearIcon
                  onClick={(e) => clearDocument(e, "selfie")}
                  sx={{
                    fontSize: "20px",
                    cursor: "pointer",
                    "&:hover": { opacity: 0.7 },
                  }}
                />
              )}
              <input
                id="selfie-upload"
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => onImageChange(e, "selfie")}
              />
            </Button>

            {selfiePreview && (
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <img
                  src={selfiePreview}
                  alt="Selfie preview"
                  style={{ maxWidth: "50px", maxHeight: "80px", borderRadius: "8px" }}
                />
              </Box>
            )}

            <Typography className="mb-2" variant="subtitle1" align="left">
              Upload Driving License/Passport (Front)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{
                marginBottom: 2,
                borderColor: "#4caf50",
                color: "black",
                textTransform: "capitalize",
                justifyContent: "space-between",
                padding: "10px",
              }}
            >
              <span>{documentFile ? documentFile.name : "Choose File"}</span>
              {documentFile && (
                <ClearIcon
                  onClick={(e) => clearDocument(e, "document")}
                  sx={{
                    fontSize: "20px",
                    cursor: "pointer",
                    "&:hover": { opacity: 0.7 },
                  }}
                />
              )}
              <input
                id="document-upload"
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => onImageChange(e, "document")}
              />
            </Button>

            {documentPreview && (
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <img
                  src={documentPreview}
                  alt="Document preview"
                  style={{ maxWidth: "50px", maxHeight: "80px", borderRadius: "8px" }}
                />
              </Box>
            )}

            <Typography className="mb-2" variant="subtitle1" align="left">
              Upload Driving License/Passport (Back)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{
                marginBottom: 2,
                borderColor: "#4caf50",
                color: "black",
                textTransform: "capitalize",
                justifyContent: "space-between",
                padding: "10px",
              }}
            >
              <span>{backDocumentFile ? backDocumentFile.name : "Choose File"}</span>
              {backDocumentFile && (
                <ClearIcon
                  onClick={(e) => clearDocument(e, "backDocument")}
                  sx={{
                    fontSize: "20px",
                    cursor: "pointer",
                    "&:hover": { opacity: 0.7 },
                  }}
                />
              )}
              <input
                id="back-document-upload"
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => onImageChange(e, "backDocument")}
              />
            </Button>

            {backDocumentPreview && (
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <img
                  src={backDocumentPreview}
                  alt="Back document preview"
                  style={{ maxWidth: "50px", maxHeight: "50px", borderRadius: "8px" }}
                />
              </Box>
            )}

            <Typography className="mb-2" variant="subtitle1" align="left">
              Document Type
            </Typography>
            <FormControl fullWidth sx={{ marginBottom: 2 }}>
              <Select
                id="doc-type-select"
                displayEmpty
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                inputProps={{
                  style: { paddingLeft: '10px' }
                }}
                sx={{
                  backgroundColor: "#ffffff",
                  color: "#333333",
                  borderRadius: "4px",
                  paddingLeft: "10px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#4caf50",
                    borderWidth: "1px",
                    paddingLeft: "10px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#4caf50",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#4caf50",
                    borderWidth: "2px",
                  },
                  "& .MuiSelect-select": {
                    paddingTop: "12px",
                    paddingBottom: "12px",
                    paddingLeft: "20px",
                    paddingRight: "19px",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#4caf50",
                  },
                }}
              >
                <MenuItem value="" disabled sx={{ color: "black" }}>
                  Select Document Type
                </MenuItem>
                <MenuItem value="PASSPORT">Passport</MenuItem>
                <MenuItem value="DRIVER_LICENSE">Driver License</MenuItem>
              </Select>
            </FormControl>
          </div>

          <Button
            variant="contained"
            onClick={handleKycSubmit}
            disabled={isSubmitting}
            sx={{
              background: "#4caf50",
              color: "#ffffff",
              "&:disabled": {
                background: "#cccccc",
                color: "#666666"
              }
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit Documents"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default KycProcess;
